import React, { useState } from 'react';
import Login from './Login';
import Register from './Register';
import { useAuth } from '../hooks/useAuth';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: 'login' | 'register';
}

const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  initialMode = 'login',
}) => {
  const [mode, setMode] = useState<'login' | 'register'>(initialMode);
  const [error, setError] = useState<string>('');
  const { login, register, isLoading } = useAuth();

  if (!isOpen) return null;

  const handleLogin = async (email: string, password: string) => {
    try {
      setError('');
      await login(email, password);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de connexion');
    }
  };

  const handleRegister = async (
    email: string,
    password: string,
    name?: string
  ) => {
    try {
      setError('');
      await register(email, password, name);
      onClose();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Erreur lors de la création du compte'
      );
    }
  };

  const switchToRegister = () => {
    setMode('register');
    setError('');
  };

  const switchToLogin = () => {
    setMode('login');
    setError('');
  };

  const handleClose = () => {
    setError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-all duration-300"
        onClick={handleClose}
      />

      {/* Modal Content */}
      <div className="flex items-center justify-center min-h-screen p-4 sm:p-6">
        <div className="relative w-full max-w-md mx-auto">
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute -top-2 -right-2 z-20 bg-white hover:bg-gray-50 rounded-full w-10 h-10 flex items-center justify-center transition-all duration-200 shadow-lg border border-gray-200 text-gray-600 hover:text-gray-800"
            disabled={isLoading}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          {/* Auth Forms */}
          <div className="transform transition-all duration-300 ease-out">
            {mode === 'login' ? (
              <Login
                onLogin={handleLogin}
                onSwitchToRegister={switchToRegister}
                isLoading={isLoading}
                error={error}
              />
            ) : (
              <Register
                onRegister={handleRegister}
                onSwitchToLogin={switchToLogin}
                isLoading={isLoading}
                error={error}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
